import { Outlet, Link, useRouter } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth';

export function RootLayout() {
  const { user, logout, isLoading } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    await logout();
    router.navigate({ to: '/' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-dream-800 bg-dream-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-dream-400 to-purple-600" />
              <span className="font-display text-xl font-bold text-white">DreamPaths</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/symbols"
                className="text-dream-300 hover:text-white transition-colors"
                activeProps={{ className: 'text-white' }}
              >
                Symbols
              </Link>
              {user && (
                <>
                  <Link
                    to="/dreams"
                    className="text-dream-300 hover:text-white transition-colors"
                    activeProps={{ className: 'text-white' }}
                  >
                    My Dreams
                  </Link>
                  <Link
                    to="/subconscious"
                    className="text-dream-300 hover:text-white transition-colors"
                    activeProps={{ className: 'text-white' }}
                  >
                    Subconscious
                  </Link>
                </>
              )}
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-4">
              {isLoading ? (
                <div className="w-8 h-8 rounded-full bg-dream-800 animate-pulse" />
              ) : user ? (
                <div className="flex items-center gap-4">
                  <Link to="/dreams/new" className="btn-primary text-sm">
                    Record Dream
                  </Link>
                  <div className="relative group">
                    <button className="flex items-center gap-2">
                      {user.profilePictureUrl ? (
                        <img
                          src={user.profilePictureUrl}
                          alt={user.name || user.email}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-dream-700 flex items-center justify-center text-sm font-medium">
                          {(user.name || user.email)[0].toUpperCase()}
                        </div>
                      )}
                    </button>
                    <div className="absolute right-0 top-full mt-2 w-48 bg-dream-900 border border-dream-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-dream-200 hover:bg-dream-800 hover:text-white"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-dream-200 hover:bg-dream-800 hover:text-white"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="btn-ghost text-sm">
                    Login
                  </Link>
                  <Link to="/signup" className="btn-primary text-sm">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-dream-800 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-dream-400 text-sm">
                DreamPaths - Explore the landscape of your subconscious mind
              </p>
              <p className="text-dream-500 text-sm mt-1">
                &copy; {new Date().getFullYear()} Authentic Creative, LLC
              </p>
            </div>
            <div className="flex items-center gap-6 text-sm text-dream-400">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms
              </Link>
              <Link to="/cookies" className="hover:text-white transition-colors">
                Cookies
              </Link>
              <Link to="/faq" className="hover:text-white transition-colors">
                FAQ
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
